import React from 'react';
import { GENERAL_ZONE_LABEL } from '../../../constants/violationConstants';
import { ZONE_SCOPE_GENERAL, ZONE_SCOPE_SPECIFIC } from '../../../utils/ruleZones';

/**
 * Выбор области контроля (PART 3, раздел 5; ТЗ задача 35; FE-FIX29).
 *
 * Каталог отдаёт zones[{zone_id, display_name}] целиком — без slice/dedup:
 * это полный список зон, применимых к правилам камеры, а не подсказка.
 *
 * Part 4, раздел 5 прямо запрещает безусловное «Весь кадр»: правило без зоны
 * считается по общей области контроля камеры (zone_id: null), а не обязательно
 * по всему кадру. Опция «Общая область контроля» показывается всегда, даже
 * пока каталог грузится или зон у камеры нет.
 *
 * FE-FIX29: «Общая область контроля» и конкретные зоны взаимно исключаются.
 * При переключении на общую область конкретные чекбоксы гасятся и очищаются,
 * при выборе любой зоны общая область снимается.
 *
 * zone_id берётся только из данных бэкенда. Синтезировать его из индекса
 * массива запрещено: «Зона 1» — это display_name, а не идентичность.
 *
 * Поддерживает ли выбранная метрика несколько зон — решает backend: каталог
 * такого признака не отдаёт, поэтому фронт не угадывает ограничение, а
 * показывает ошибку сохранения/активации как есть.
 */
const ZoneSelector = ({
  zones,
  value,
  onChange,
  disabled = false,
  loading = false,
  error = '',
}) => {
  const options = Array.isArray(zones) ? zones.filter((zone) => zone?.zone_id) : [];
  const scope = value?.scope === ZONE_SCOPE_SPECIFIC ? ZONE_SCOPE_SPECIFIC : ZONE_SCOPE_GENERAL;
  const zoneIds = Array.isArray(value?.zoneIds) ? value.zoneIds : [];

  const selectGeneral = () => onChange({ scope: ZONE_SCOPE_GENERAL, zoneIds: [] });

  const toggleZone = (zoneId) => {
    const next = zoneIds.includes(zoneId)
      ? zoneIds.filter((item) => item !== zoneId)
      : [...zoneIds, zoneId];

    // Снятие последней зоны не возвращает правило в общую область молча:
    // scope остаётся specific, и форма попросит выбрать зону явно.
    onChange({ scope: ZONE_SCOPE_SPECIFIC, zoneIds: next });
  };

  const specificDisabled = disabled || loading || options.length === 0;

  return (
    <fieldset className="vio-field vio-zone-scope">
      <legend className="vio-field-label">Область контроля</legend>

      <label className="vio-zone-scope-option">
        <input
          type="radio"
          name="vio-zone-scope"
          checked={scope === ZONE_SCOPE_GENERAL}
          disabled={disabled || loading}
          onChange={selectGeneral}
        />
        <span>
          <strong>{GENERAL_ZONE_LABEL}</strong>
        </span>
      </label>

      <label className="vio-zone-scope-option">
        <input
          type="radio"
          name="vio-zone-scope"
          checked={scope === ZONE_SCOPE_SPECIFIC}
          disabled={specificDisabled}
          onChange={() => onChange({ scope: ZONE_SCOPE_SPECIFIC, zoneIds })}
        />
        <span>
          <strong>Выбранные зоны</strong>
        </span>
      </label>

      <div className="vio-zone-scope-list">
        {loading && <p className="vio-field-hint">Загружаем зоны…</p>}
        {!loading && options.length === 0 && (
          <p className="vio-field-hint">
            {`Для выбранной камеры зоны не заданы — доступна только «${GENERAL_ZONE_LABEL}».`}
          </p>
        )}
        {options.map((zone) => (
          <label key={zone.zone_id} className="vio-zone-scope-zone">
            <input
              type="checkbox"
              checked={scope === ZONE_SCOPE_SPECIFIC && zoneIds.includes(zone.zone_id)}
              disabled={specificDisabled || scope === ZONE_SCOPE_GENERAL}
              onChange={() => toggleZone(zone.zone_id)}
            />
            <span>{zone.display_name || zone.zone_id}</span>
          </label>
        ))}
      </div>

      {error && <p className="vio-field-error">{error}</p>}
    </fieldset>
  );
};

export default ZoneSelector;
